import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { formatDateDMY } from '../utils/dateUtils';
import { useSortableTable, TABLE_HEAD_ROW, TABLE_HEAD_TEXT, TABLE_ROW_LINE } from './UI/tableSort';
import { toCurrency } from '../utils/adminMetrics';

// The API sends the rate as a fraction (0.05); shown as a percentage.
const percent = (rate) => {
  const n = Number(rate);
  return Number.isFinite(n) ? `${Number((n * 100).toFixed(2))}%` : '-';
};

// Penalty interest on a loan, month by month, as the API charged it
// (GET /api/loans/:id/penalty-breakdown: NAMFISA GN 263/2018). Nothing is
// worked out here: the rule, each month, the total line and the next charge
// date are the API's. Pass `breakdown` when it is already loaded (e.g.
// invoices[].penalty_breakdown); otherwise it is fetched by `loanId`.
// With no months charged it shows nothing, or the API's summary when
// `showEmptySummary` is set.
const PenaltyBreakdown = ({ loanId = null, breakdown, showEmptySummary = false, className = '' }) => {
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(false);
  const given = breakdown !== undefined;

  useEffect(() => {
    setFetched(null);
    if (given || !loanId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl(`/api/loans/${loanId}/penalty-breakdown`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled) setFetched(res.data || null);
      } catch {
        // Not available — nothing to show.
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [loanId, given]);

  const data = given ? breakdown : fetched;
  // Month by month until a column header is clicked.
  const table = useSortableTable(Array.isArray(data?.months) ? data.months : []);
  const hc = { className: `px-3 py-2 ${TABLE_HEAD_TEXT}` };
  const hr = { ...hc, align: 'right' };
  if (!data) {
    return loading ? (
      <div className={`text-sm text-gray-600 dark:text-slate-400 ${className}`}>Loading penalty interest...</div>
    ) : null;
  }
  const months = Array.isArray(data.months) ? data.months : [];
  if (!months.length) {
    return showEmptySummary && data.summary ? (
      <div className={`text-sm text-gray-600 dark:text-slate-400 ${className}`}>{data.summary}</div>
    ) : null;
  }

  return (
    <div className={className}>
      <div className="bg-white dark:bg-slate-800 overflow-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className={TABLE_HEAD_ROW}>
              {table.th('Month', 'month', (m) => Number(m.penalty_month_no), hc)}
              {table.th('Charged on', 'charged', (m) => m.charge_date, hc)}
              {table.th('Arrears (N$)', 'arrears', (m) => Number(m.arrears_base), hr)}
              {table.th('Rate', 'rate', (m) => Number(m.rate), hr)}
              {table.th('Penalty (N$)', 'penalty', (m) => Number(m.penalty_amount), hr)}
            </tr>
          </thead>
          <tbody className="text-gray-800 dark:text-slate-200">
            {table.rows.map((m) => (
              <tr key={m.penalty_month_no} className={TABLE_ROW_LINE}>
                <td className="px-3 py-2">Month {m.penalty_month_no}</td>
                <td className="px-3 py-2">{formatDateDMY(m.charge_date)}</td>
                <td className="px-3 py-2 text-right">{toCurrency(m.arrears_base)}</td>
                <td className="px-3 py-2 text-right">{percent(m.rate)}</td>
                <td className="px-3 py-2 text-right font-medium text-red-600 dark:text-red-400">{toCurrency(m.penalty_amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-b border-gray-200 dark:border-slate-700 bg-yellow-50 dark:bg-yellow-900/20 font-semibold text-gray-900 dark:text-slate-100">
              <td className="px-3 py-2" colSpan={4}>
                Total penalty
                {data.cap_reached && (
                  <span className="ml-2 text-xs font-normal text-yellow-800 dark:text-yellow-300">maximum 3-month period reached</span>
                )}
              </td>
              <td className="px-3 py-2 text-right text-red-700 dark:text-red-300">{toCurrency(data.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">
        {data.rule || 'Penalty interest (NAMFISA GN 263/2018): 5% per month on the arrear instalments, at most 3 months over the life of the loan.'}
        {data.next_charge_date && (
          <> Next charge date: {formatDateDMY(data.next_charge_date)} if still in arrears.</>
        )}
      </div>
    </div>
  );
};

export default PenaltyBreakdown;
